import { useParentGoalSearch } from "@/models/goals/useParentGoalSearch";
import { PageModule } from "@/routes/types";
import * as React from "react";
import { useNavigate } from "react-router";

import { accessLevelsAsNumbers, accessLevelsAsStrings, parseParentGoalForTurboUi } from "@/models/goals";
import * as People from "@/models/people";
import * as Projects from "@/models/projects";
import { useCreateProjectTemplateFromProject } from "@/models/projectTemplates";
import * as Tasks from "@/models/tasks";
import * as Time from "@/utils/time";

import { Feed, useFeedItemsQuery } from "@/features/Feed";
import { useInvalidateProjectPage } from "@/models/projects/projectPageQueries";
import { useProjectUpdates } from "@/models/projects/useProjectUpdates";
import { ProjectPage, showErrorToast } from "turboui";
import { loader, useLoadedData, useRefreshCore } from "./loader";
import { useResourceHubDocsQueries, type ResourceHubDocsAndFilesData } from "@/models/resourceHubs/docsQueries";
import { useProjectContentQueries } from "./contentQueries";
import { shouldRevalidate } from "./navigation";

import { parseMilestoneForTurboUi, parseMilestonesForTurboUi } from "@/models/milestones";
import { useMilestoneCompletion } from "@/models/milestones/useMilestoneCompletion";
import { parseCheckInsForTurboUi } from "@/models/projectCheckIns";
import * as Spaces from "@/models/spaces";
import { Paths, usePaths } from "@/routes/paths";
import { parseContextualDate, serializeContextualDate } from "../../models/contextualDates";
import { useRichTextHandlers } from "@/hooks/useRichTextHandlers";
import { useFormattedTimePreferences } from "@/hooks/useFormattedTimePreferences";
import { useMe } from "@/contexts/CurrentCompanyContext";
import i18n, { translationText } from "@/i18n";
import {
  useAddFileWidgetProps,
  useNewFileModalsContextValue,
  useResourceHubNodesListProps,
  useCreateFolder,
} from "@/models/resourceHubs";
import { useSubscription } from "@/models/subscriptions";
import { useResourceHubSearchProps } from "@/models/search/resourceHub";

export default { name: "ProjectPage", loader, Page, shouldRevalidate } as PageModule;

function Page() {
  const { projectInput } = useLoadedData();
  // Response IDs include the project name. A rename must not remount editors.
  return <LoadedPage key={projectInput.id} />;
}

function LoadedPage() {
  const navigate = useNavigate();
  const paths = usePaths();
  const currentUser = useMe();
  const { data, projectInput, checkInsInput, discussionsInput, tasksInput } = useLoadedData();
  const { project, childrenCount, space } = data;
  useProjectUpdates(projectInput.id, project.resourceHub?.id);

  const refreshCore = useRefreshCore();
  const invalidateProjectPage = useInvalidateProjectPage();

  const docs = useResourceHubDocsQueries(data.project.resourceHub?.id);
  const docsAndFiles = docs.data;

  const content = useProjectContentQueries({ checkInsInput, discussionsInput, tasksInput });
  const { checkIns, discussions, backendTasks } = content;

  const refresh = React.useCallback(async () => {
    await Promise.all([refreshCore(), content.refresh(), docs.refresh()]);
  }, [refreshCore, content.refresh, docs.refresh]);

  const milestoneCompletion = useMilestoneCompletion();
  const updatePermissions = Projects.useUpdateProjectPermissions();
  const updateKanban = Projects.useUpdateProjectKanban();
  const updateTasksView = Projects.useUpdateProjectTasksView();
  const updateProjectName = Projects.useUpdateProjectName();
  const updateProjectDescription = Projects.useUpdateProjectDescription();
  const updateProjectStartDate = Projects.useUpdateProjectStartDate();
  const updateProjectDueDate = Projects.useUpdateProjectDueDate();
  const updateProjectParentGoal = Projects.useUpdateProjectParentGoal();
  const deleteProjectMutation = Projects.useDeleteProject();
  const createTemplateFromProject = useCreateProjectTemplateFromProject();

  const transformPerson = React.useCallback((p) => People.parsePersonForTurboUi(paths, p)!, [paths]);
  const { spaceProps, champion, reviewer, updateChampion, updateReviewer } = useSpaceProps({
    project,
    paths,
    transformPerson,
  });
  const backLink = "workmapLink" in spaceProps ? spaceProps.workmapLink : spaceProps.homeLink;

  const [projectName, setProjectName] = usePageField({
    value: (data) => data.project.name!,
    update: (v) => updateProjectName.mutateAsync({ projectId: project.id, name: v }).then(() => true),
    onError: (e: string) => showErrorToast(e, i18n.t("Reverted the project name to its previous value.")),
    validations: [(v) => (v.trim() === "" ? translationText(i18n.t("Project name cannot be empty")) : null)],
  });

  const [description, setDescription] = usePageField({
    value: (data: { project: Projects.Project }) => data.project.description && JSON.parse(data.project.description),
    update: (v) =>
      updateProjectDescription.mutateAsync({ projectId: project.id, description: JSON.stringify(v) }).then(() => true),
    onError: () => showErrorToast(i18n.t("Network Error"), i18n.t("Reverted the description to its previous value.")),
  });

  const [parentGoal, setParentGoal] = usePageField({
    value: (data: { project: Projects.Project }) => parseParentGoalForTurboUi(paths, data.project.goal),
    update: (v) =>
      updateProjectParentGoal.mutateAsync({
        projectId: project.id,
        goalId: v && v.id,
      }),
    onError: () => showErrorToast(i18n.t("Network Error"), i18n.t("Reverted the parent goal to its previous value.")),
  });

  const [accessLevels, setAccessLevels] = usePageField({
    value: (data) => accessLevelsAsStrings(data.project.accessLevels!),
    update: (v) =>
      updatePermissions
        .mutateAsync({
          projectId: project.id,
          accessLevels: {
            ...accessLevelsAsNumbers(v),
            public: project.accessLevels?.public ?? 0,
          },
        })
        .then(() => true),
    onError: () =>
      showErrorToast(i18n.t("Network Error"), i18n.t("Reverted the access levels to their previous values.")),
  });

  const [dueDate, setDueDate] = usePageField({
    value: (data: { project: Projects.Project }) => parseContextualDate(data.project.timeframe?.contextualEndDate),
    update: (v) => updateProjectDueDate.mutateAsync({ projectId: project.id, dueDate: serializeContextualDate(v) }),
    onError: () => showErrorToast(i18n.t("Network Error"), i18n.t("Reverted the due date to its previous value.")),
  });

  const [startedDate, setStartedDate] = usePageField({
    value: (data: { project: Projects.Project }) => parseContextualDate(data.project.timeframe?.contextualStartDate),
    update: (v) => updateProjectStartDate.mutateAsync({ projectId: project.id, startDate: serializeContextualDate(v) }),
    onError: () => showErrorToast(i18n.t("Network Error"), i18n.t("Reverted the started date to its previous value.")),
  });

  const {
    milestones,
    filteredMilestones,
    setMilestones,
    createMilestone,
    updateMilestone,
    reorderMilestones,
    searchMilestones,
  } = useMilestones(paths, project, refresh);

  const {
    tasks: baseTasks,
    setTasks,
    createTask,
    updateTaskName,
    updateTaskDueDate,
    updateTaskReminders,
    updateTaskAssignee,
    updateTaskDescription,
    updateTaskMilestone,
    deleteTask,
    updateTaskArchive,
  } = Tasks.useProjectTasksForTurboUi({
    backendTasks,
    tasksLoaded: content.tasksLoaded,
    projectId: project.id,
    milestones,
    setMilestones,
    refresh,
  });

  const subscriptions = useSubscription({
    subscriptionList: project.subscriptionList,
    entityId: project.id,
    entityType: "project",
    onRefresh: refresh,
  });

  const parentGoalSearch = useParentGoalSearch({ type: "project", id: project.id });
  const richTextHandlers = useRichTextHandlers({
    taskList: {
      resourceType: "project",
      resourceId: project.id,
      field: "description",
      canEdit: project.permissions?.canEdit ?? false,
    },
    scope: { type: "project", id: project.id },
  });
  const formattedTimePreferences = useFormattedTimePreferences();

  const assigneePersonSearch = Tasks.useTaskAssigneeSearch({
    id: project.id,
    type: "project",
    transformResult: transformPerson,
  });

  const { statuses, handleSaveStatuses } = Projects.useTaskStatuses(project.id, project.taskStatuses, refresh);

  const handleTasksViewChange = React.useCallback(
    async (tasksView: "list" | "board") => {
      try {
        await updateTasksView.mutateAsync({
          projectId: project.id,
          tasksView,
        });

        void invalidateProjectPage(project.id);

        if (refresh) {
          await refresh();
        }
      } catch (error) {
        console.error("Failed to update tasks view", error);
        showErrorToast(i18n.t("Error"), i18n.t("Failed to update tasks view"));

        if (refresh) {
          await refresh();
        }
      }
    },
    [project.id, refresh],
  );

  const taskProjectSearch = Projects.useProjectSearch({
    accessLevel: "edit_access",
    ignoredIds: [project.id],
    activeOnly: true,
  });
  const taskSpaceSearch = Spaces.useSpaceSearch({ accessLevel: "edit_access", withTasksEnabledOnly: true });

  const handleMoveTaskSuccess = React.useCallback(
    ({ destinationType, destinationId }: Tasks.TaskMoveDestination) => {
      void invalidateProjectPage(project.id);

      if (destinationType === "project") {
        void invalidateProjectPage(destinationId);
      }
    },
    [project.id],
  );

  const { kanbanState, handleTaskKanbanChange, handleTaskStatusChange } = Tasks.useKanbanState({
    initialRawState: project.tasksKanbanState,
    updateKanban: updateKanban.mutateAsync,
    statuses,
    projectId: project.id,
    type: "project",
    tasks: baseTasks,
    setTasks,
    onSuccess: async () => {
      void invalidateProjectPage(project.id);

      if (refresh) {
        await refresh();
      }
    },
  });

  const slideInModel = Tasks.useTaskSlideInProps({
    backendTasks,
    paths,
    currentUser,
    tasks: baseTasks,
    commentEntityType: "project_task",
    onRefresh: refresh,
    canEdit: Boolean(project.permissions?.canEdit),
    canComment: Boolean(project.permissions?.canComment),
    variant: "project-task",
    onTaskNameChange: updateTaskName,
    onTaskAssigneeChange: updateTaskAssignee,
    onTaskDueDateChange: updateTaskDueDate,
    onTaskRemindersChange: updateTaskReminders,
    onTaskStatusChange: handleTaskStatusChange,
    onTaskDescriptionChange: updateTaskDescription,
    onMoveTaskSuccess: handleMoveTaskSuccess,
    projectSearch: taskProjectSearch,
    spaceSearch: taskSpaceSearch,
  });

  const deleteProject = async () => {
    return deleteProjectMutation
      .mutateAsync({ projectId: project.id })
      .then(() => {
        void invalidateProjectPage(project.id);
        navigate(backLink);

        return { success: true };
      })
      .catch((e) => {
        console.error("Failed to delete project", e);
        showErrorToast(i18n.t("Error"), i18n.t("Failed to delete project"));

        return { success: false };
      });
  };

  const contributorActions = Projects.useProjectContributorActions({
    project,
  });

  const { includeDemotedContributor, excludePromotedContributor, restoreContributor, ...contributorPageActions } =
    contributorActions;

  const setChampion = React.useCallback(
    async (person: ProjectPage.Person | null) => {
      const previous = champion;
      let removed: ReturnType<typeof excludePromotedContributor> = null;

      if (!person && previous?.id) {
        includeDemotedContributor(previous.id);
      } else if (person?.id) {
        removed = excludePromotedContributor(person.id);
      }

      const ok = await updateChampion(person);
      if (ok) return;

      if (!person && previous?.id) {
        excludePromotedContributor(previous.id);
      } else if (removed) {
        restoreContributor(removed);
      }
    },
    [champion, excludePromotedContributor, includeDemotedContributor, restoreContributor, updateChampion],
  );

  const setReviewer = React.useCallback(
    async (person: ProjectPage.Person | null) => {
      const previous = reviewer;
      let removed: ReturnType<typeof excludePromotedContributor> = null;

      if (!person && previous?.id) {
        includeDemotedContributor(previous.id);
      } else if (person?.id) {
        removed = excludePromotedContributor(person.id);
      }

      const ok = await updateReviewer(person);
      if (ok) return;

      if (!person && previous?.id) {
        excludePromotedContributor(previous.id);
      } else if (removed) {
        restoreContributor(removed);
      }
    },
    [excludePromotedContributor, includeDemotedContributor, restoreContributor, reviewer, updateReviewer],
  );

  const assignedPersonIds = React.useMemo(
    () => [
      champion?.id,
      reviewer?.id,
      ...contributorPageActions.contributors.map((contributor) => contributor.person?.id),
    ],
    [champion?.id, reviewer?.id, contributorPageActions.contributors],
  );

  const otherPeopleWithAccess = Projects.useProjectOtherPeopleWithAccess({
    projectId: project.id,
    assignedPersonIds,
  });

  const exportMarkdown = React.useCallback(() => {
    window.open(paths.projectMarkdownExportPath(project.id), "_blank", "noopener");
  }, [paths, project.id]);

  const saveAsTemplate = {
    canSave: Boolean(space?.permissions?.canEdit),
    submissionEnabled: true,
    onSave: Projects.createSaveProjectAsTemplateHandler({
      projectId: project.id,
      paths,
      createFromProject: createTemplateFromProject.mutateAsync,
      navigate,
    }),
  };

  const projectDocsAndFilesProps = useProjectDocsAndFilesProps({
    docsAndFiles,
    projectId: project.id,
    onRefresh: refresh,
  });

  const props: ProjectPage.Props = {
    ...spaceProps,
    champion,
    reviewer,
    ...("setChampion" in spaceProps ? { setChampion } : {}),
    ...("setReviewer" in spaceProps ? { setReviewer } : {}),
    closeLink: paths.projectClosePath(project.id),
    reopenLink: paths.resumeProjectPath(project.id),
    pauseLink: paths.pauseProjectPath(project.id),
    exportMarkdown,
    saveAsTemplate,
    childrenCount,

    project: {
      id: project.id,
      name: projectName,
    },
    updateProjectName: (name: string) => {
      setProjectName(name);
      return Promise.resolve(true);
    },

    description,
    onDescriptionChange: setDescription,

    parentGoal,
    setParentGoal,
    parentGoalSearch,

    startedAt: startedDate,
    setStartedAt: setStartedDate,
    dueAt: dueDate,
    setDueAt: setDueDate,

    status: project.status,
    state: project.state,
    closedAt: Time.parse(project.closedAt),
    retrospectiveLink: paths.projectRetrospectivePath(project.id),

    permissions: project.permissions || {},
    otherPeopleWithAccess,
    accessLevels,
    setAccessLevels,

    onProjectDelete: deleteProject,

    tasks: baseTasks,
    kanbanState,
    onTaskKanbanChange: handleTaskKanbanChange,
    onTaskCreate: createTask,
    onTaskNameChange: slideInModel.onTaskNameChange,
    onTaskDueDateChange: slideInModel.onTaskDueDateChange,
    onTaskAssigneeChange: slideInModel.onTaskAssigneeChange,
    onTaskStatusChange: slideInModel.onTaskStatusChange,
    onTaskRemindersChange: slideInModel.onTaskRemindersChange,
    onTaskMilestoneChange: updateTaskMilestone,
    onTaskDelete: deleteTask,
    onTaskArchiveChange: updateTaskArchive,
    onTaskDescriptionChange: slideInModel.onTaskDescriptionChange,
    getTaskPageProps: slideInModel.getTaskPageProps,
    milestones,
    searchableMilestones: filteredMilestones,
    onMilestoneSearch: searchMilestones,
    onMilestoneComplete: milestoneCompletion.requestCompletion,
    completeMilestoneModal: milestoneCompletion.modalProps,
    onMilestoneCreate: createMilestone,
    onMilestoneUpdate: updateMilestone,
    onMilestoneReorder: reorderMilestones,
    ...contributorPageActions,

    docsAndFilesAvailable: docs.available,
    docsAndFilesLoading: docs.loading,
    docsAndFilesError: docs.error,

    onRetryDocsAndFiles: docs.retry,
    tasksLoading: content.tasksLoading,
    tasksError: content.tasksError,
    onRetryTasks: content.retryTasks,

    checkIns: parseCheckInsForTurboUi(paths, checkIns),
    checkInsLoading: content.checkInsLoading,
    checkInsError: content.checkInsError,
    onRetryCheckIns: content.retryCheckIns,
    newCheckInLink: paths.projectCheckInNewPath(project.id),
    nextCheckInScheduledAt: Time.parse(project.nextCheckInScheduledAt),

    discussions: prepareDiscussions(paths, discussions),
    discussionsLoading: content.discussionsLoading,
    discussionsError: content.discussionsError,
    onRetryDiscussions: content.retryDiscussions,
    newDiscussionLink: paths.projectDiscussionNewPath(project.id),

    currentUser: currentUser ? People.parsePersonForTurboUi(paths, currentUser) : null,
    assigneePersonSearch: assigneePersonSearch,

    statuses,
    onSaveCustomStatuses: handleSaveStatuses,
    tasksView: project.tasksView === "board" ? "board" : "list",
    onTasksViewChange: handleTasksViewChange,

    richTextHandlers,
    localDraftKeyBase: `project:${projectInput.id}`,

    activityFeed: <ProjectFeedItems projectId={project.id} />,

    subscriptions,
    docsAndFiles: projectDocsAndFilesProps,
    formattedTimePreferences,
  };

  return <ProjectPage {...props} />;
}

function useProjectDocsAndFilesProps({
  docsAndFiles,
  projectId,
  onRefresh,
}: {
  docsAndFiles: ResourceHubDocsAndFilesData | null;
  projectId: string;
  onRefresh?: () => Promise<void>;
}): ProjectPage.Props["docsAndFiles"] {
  const paths = usePaths();
  const resourceHub = docsAndFiles?.resourceHub;
  const refresh = React.useCallback(() => {
    void onRefresh?.();
  }, [onRefresh]);
  const newFileModals = useNewFileModalsContextValue({ resourceHub });
  const addFileWidgetProps = useAddFileWidgetProps({ resourceHub, onUploaded: refresh });
  const { mutateAsync: createFolder } = useCreateFolder();
  const nodesListProps = useResourceHubNodesListProps(
    resourceHub
      ? {
          resourceHub,
          type: "resource_hub",
          nodes: docsAndFiles?.nodes || [],
          refetch: refresh,
        }
      : null,
  );
  const search = useResourceHubSearchProps(resourceHub?.id);

  return React.useMemo(() => {
    if (!docsAndFiles || !resourceHub?.id) {
      return undefined;
    }

    return {
      resourceHub,
      previewNodes: docsAndFiles.nodes,
      tabPath: paths.projectPath(projectId, { tab: "docs-and-files" }),
      drafts: {
        nodes: docsAndFiles.draftNodes,
        draftsPath: paths.resourceHubDraftsPath(resourceHub.id),
      },
      newFileModals,
      addFileWidgetProps,
      nodesListProps,
      search,
      addFolderModalProps: {
        resourceHubId: resourceHub.id,
        onCreated: refresh,
        onCreateFolder: async (args) => {
          await createFolder({
            resourceHubId: args.resourceHubId,
            folderId: args.folderId,
            name: args.name,
          });
        },
      },
    };
  }, [
    addFileWidgetProps,
    createFolder,
    docsAndFiles,
    newFileModals,
    nodesListProps,
    paths,
    projectId,
    refresh,
    resourceHub,
    search,
  ]);
}

function ProjectFeedItems({ projectId }: { projectId: string }) {
  const { data, loading, error, pagination } = useFeedItemsQuery("project", projectId);

  if (loading) return null;
  if (error) return null;

  return <Feed pagination={pagination} items={data?.activities || []} page="project" testId="project-feed" />;
}

interface usePageFieldProps<T> {
  value: (data: ReturnType<typeof useLoadedData>["data"]) => T;
  update: (newValue: T) => Promise<{ success?: boolean | null } | boolean | null | undefined>;
  onError?: (error: any) => void;
  validations?: ((newValue: T) => string | null)[];
}

function usePageField<T>({
  value,
  update,
  onError,
  validations,
}: usePageFieldProps<T>): [T, (v: T) => Promise<boolean>] {
  const { data } = useLoadedData();
  const invalidateProjectPage = useInvalidateProjectPage();
  const pendingSaves = React.useRef(0);
  const valueRef = React.useRef(value);
  valueRef.current = value;
  const [state, setState] = React.useState<T>(() => value(data));
  React.useEffect(() => {
    if (pendingSaves.current === 0) setState(valueRef.current(data));
  }, [data]);

  const updateState = (newVal: T): Promise<boolean> => {
    return new Promise((resolve) => {
      if (validations) {
        for (const validation of validations) {
          const error = validation(newVal);
          if (error) {
            onError?.(error);
            resolve(false);
            return;
          }
        }
      }

      const oldVal = state;
      pendingSaves.current += 1;

      const successHandler = () => {
        pendingSaves.current -= 1;
        void invalidateProjectPage(data.project.id);
        resolve(true);
      };

      const errorHandler = (error: any) => {
        pendingSaves.current -= 1;
        setState(oldVal);
        onError?.(error);
        resolve(false);
      };

      setState(newVal);

      update(newVal)
        .then((res) => {
          if (res === false || (typeof res === "object" && res?.success === false)) {
            errorHandler(i18n.t("Update failed"));
          } else {
            successHandler();
          }
        })
        .catch(errorHandler);
    });
  };

  return [state, updateState];
}

function useSpaceProps({
  project,
  paths,
  transformPerson,
}: {
  project: Projects.Project;
  paths: Paths;
  transformPerson: (p: any) => ProjectPage.Person;
}): {
  spaceProps: ProjectPage.SpaceProps;
  champion: ProjectPage.Person | null;
  reviewer: ProjectPage.Person | null | undefined;
  updateChampion: (person: ProjectPage.Person | null) => Promise<boolean>;
  updateReviewer: (person: ProjectPage.Person | null) => Promise<boolean>;
} {
  const moveProject = Projects.useMoveProjectToSpace();
  const updateChampionMutation = Projects.useUpdateProjectChampion();
  const updateReviewerMutation = Projects.useUpdateProjectReviewer();
  const [space, setSpace] = usePageField({
    value: (data) => (data.space ? Spaces.parseSpaceForTurboUI(paths, data.space) : null),
    update: (v) => moveProject.mutateAsync({ projectId: project.id, spaceId: v!.id }).then(() => true),
    onError: () => showErrorToast(i18n.t("Network Error"), i18n.t("Reverted the space to its previous value.")),
  });

  const [champion, updateChampion] = usePageField<ProjectPage.Person | null>({
    value: (data) => People.parsePersonForTurboUi(paths, data.project.champion),
    update: (v) => updateChampionMutation.mutateAsync({ projectId: project.id, championId: v?.id ?? null }),
    onError: () => showErrorToast(i18n.t("Network Error"), i18n.t("Reverted the champion to its previous value.")),
  });

  const [reviewer, updateReviewer] = usePageField<ProjectPage.Person | null>({
    value: (data) => People.parsePersonForTurboUi(paths, data.project.reviewer),
    update: (v) => updateReviewerMutation.mutateAsync({ projectId: project.id, reviewerId: v?.id ?? null }),
    onError: () => showErrorToast(i18n.t("Network Error"), i18n.t("Reverted the reviewer to its previous value.")),
  });

  const spaceSearch = Spaces.useSpaceSearch();

  const ignoredIds = React.useMemo(
    () => [champion?.id, reviewer?.id].filter((id): id is string => Boolean(id)),
    [champion?.id, reviewer?.id],
  );

  const searchScope = space ? { type: "space" as const, id: space.id } : { type: "company" as const };

  const championSearch = People.usePersonFieldSearch({
    scope: searchScope,
    ignoredIds,
    transformResult: transformPerson,
  });

  const reviewerSearch = People.usePersonFieldSearch({
    scope: searchScope,
    ignoredIds,
    transformResult: transformPerson,
  });

  if (!space) {
    return {
      spaceProps: {
        homeLink: paths.homePath(),
      },
      champion,
      reviewer,
      updateChampion,
      updateReviewer,
    };
  }

  return {
    spaceProps: {
      workmapLink: paths.spaceWorkMapPath(space.id, "projects" as const),
      space: space as ProjectPage.Space,
      setSpace: setSpace as any,
      spaceSearch,

      setChampion: updateChampion,
      championSearch,

      setReviewer: updateReviewer,
      reviewerSearch,
    },
    champion,
    reviewer,
    updateChampion,
    updateReviewer,
  };
}

function prepareDiscussions(paths: Paths, discussions: Projects.Discussion[]): ProjectPage.Discussion[] {
  return discussions.map((discussion) => {
    return {
      id: discussion.id,
      date: Time.parse(discussion.insertedAt)!,
      title: discussion.title || "",
      author: People.parsePersonForTurboUi(paths, discussion.author),
      link: paths.projectDiscussionPath(discussion.id),
      content: JSON.parse(discussion.message || "{}"),
      commentCount: discussion.commentsCount || 0,
    };
  });
}

function useMilestones(paths: Paths, project: Projects.Project, refresh?: () => Promise<void>) {
  const invalidateProjectPage = useInvalidateProjectPage();
  const createMilestoneMutation = Projects.useCreateProjectMilestone();
  const updateMilestoneMutation = Projects.useUpdateProjectMilestone();
  const parsedMilestones = React.useMemo(
    () => parseMilestonesForTurboUi(paths, project.milestones || [], project.milestonesOrderingState || []),
    [paths, project.milestones, project.milestonesOrderingState],
  );

  const { milestones, setMilestones, reorderMilestones, orderingState } = Projects.useProjectMilestoneOrdering({
    projectId: project.id,
    refresh,
    initialMilestones: parsedMilestones.orderedMilestones,
    initialOrderingState: parsedMilestones.orderingState,
  });

  // Separate filtered list for MilestoneField - doesn't affect the main milestones list
  const [filteredMilestones, setFilteredMilestones] = React.useState(milestones);

  // Keep filtered list in sync with main milestones list
  React.useEffect(() => {
    setFilteredMilestones(milestones);
  }, [milestones]);

  const createMilestone = async (milestone: ProjectPage.NewMilestonePayload) => {
    return createMilestoneMutation
      .mutateAsync({
        projectId: project.id,
        name: milestone.name,
        dueDate: serializeContextualDate(milestone.dueDate),
      })
      .then((data) => {
        const createdMilestone = parseMilestoneForTurboUi(paths, data.milestone);

        void invalidateProjectPage(project.id);
        setMilestones((prev) => [...prev, createdMilestone]);

        return { success: true, milestone: createdMilestone };
      })
      .catch((e) => {
        console.error("Failed to create milestone", e);
        showErrorToast(i18n.t("Error"), i18n.t("Failed to create milestone"));

        return { success: false };
      });
  };

  const updateMilestone = async (milestoneId: string, updates: ProjectPage.UpdateMilestonePayload) => {
    return updateMilestoneMutation
      .mutateAsync({
        projectId: project.id,
        milestoneId: milestoneId,
        name: updates.name,
        dueDate: serializeContextualDate(updates.dueDate),
      })
      .then((data) => {
        void invalidateProjectPage(project.id);
        setMilestones((prev) =>
          prev.map((m) => {
            if (m.id === milestoneId) {
              return parseMilestoneForTurboUi(paths, data.milestone);
            }
            return m;
          }),
        );

        return { success: true };
      })
      .catch((e) => {
        console.error("Failed to update milestone", e);
        showErrorToast(i18n.t("Error"), i18n.t("Failed to update milestone"));

        return { success: false };
      });
  };

  const searchMilestones = async (query: string) => {
    const trimmedQuery = query.trim().toLowerCase();

    if (!trimmedQuery) {
      // Empty query - show all milestones
      setFilteredMilestones(milestones);
    } else {
      // Filter milestones by name
      const filtered = milestones.filter((m) => m.name.toLowerCase().includes(trimmedQuery));
      setFilteredMilestones(filtered);
    }
  };

  return {
    milestones,
    filteredMilestones,
    setMilestones,
    createMilestone,
    updateMilestone,
    reorderMilestones,
    searchMilestones,
    orderingState,
  };
}
